import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import { fetchPrepCategories, type PrepCategoryWithCount, type PrepDashboardCard } from '../../../api/prep'
import {
  PREP_FETCH_RETRY_DELAYS_MS,
  computeRetryDelay,
  isRetriablePrepFetchError,
  wait,
} from './utils'

const ITEMS_PER_PAGE_OPTIONS = [3, 5, 8, 10, 12, 15]

interface PrepDashboardSettingsModalProps {
  open: boolean
  onClose: () => void
  existingCards: PrepDashboardCard[]
  onAddCategoryCards: (
    cards: Array<{
      categoryId: number
      itemsPerPage: number
      collapsed?: boolean
      categoryName?: string
      categorySlug?: string
    }>,
  ) => Promise<void>
}

type SnackbarState = { message: string; severity: 'info' | 'error' | 'success' }

const clampItemsPerPage = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 5
  }
  const normalized = Math.floor(value)
  return Math.min(15, Math.max(3, normalized))
}

const PrepDashboardSettingsModal: React.FC<PrepDashboardSettingsModalProps> = ({
  open,
  onClose,
  existingCards,
  onAddCategoryCards,
}) => {
  const [categories, setCategories] = React.useState<PrepCategoryWithCount[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [retryReady, setRetryReady] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<number[]>([])
  const [itemsPerPage, setItemsPerPage] = React.useState<number>(5)
  const [startCollapsed, setStartCollapsed] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)
  const [retryTick, setRetryTick] = React.useState(0)
  const lastGoodCategoriesRef = React.useRef<PrepCategoryWithCount[]>([])
  const retryTimerRef = React.useRef<number | null>(null)

  const titleId = React.useId()
  const descriptionId = React.useId()

  const existingCategoryIds = React.useMemo(() => {
    const ids = new Set<number>()
    existingCards.forEach((card) => {
      if (card.type === 'category' && typeof card.categoryId === 'number') {
        ids.add(card.categoryId)
      }
    })
    return ids
  }, [existingCards])

  const categoriesById = React.useMemo(() => {
    const map = new Map<number, PrepCategoryWithCount>()
    categories.forEach((category) => {
      map.set(category.id, category)
    })
    return map
  }, [categories])

  React.useEffect(() => {
    if (!open) {
      return
    }

    let cancelled = false
    const controller = new AbortController()
    if (retryTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    setLoading(true)
    setError(null)
    setRetryReady(false)

    const attemptCount = PREP_FETCH_RETRY_DELAYS_MS.length + 1

    const load = async () => {
      let finalError: unknown = null
      for (let attempt = 0; attempt < attemptCount; attempt += 1) {
        try {
          const response = await fetchPrepCategories({ scope: 'global', includeEmpty: true }, { signal: controller.signal })
          if (cancelled) {
            return
          }
          const fetched = response.categories ?? []
          lastGoodCategoriesRef.current = fetched
          setCategories(fetched)
          setError(null)
          setLoading(false)
          setRetryReady(false)
          return
        } catch (err) {
          if (cancelled || controller.signal.aborted) {
            return
          }
          finalError = err
          const hasMore = attempt < attemptCount - 1
          if (!hasMore || !isRetriablePrepFetchError(err, { allow508: true })) {
            break
          }
          const delay = computeRetryDelay(PREP_FETCH_RETRY_DELAYS_MS[attempt] ?? 0)
          if (delay > 0) {
            await wait(delay)
          }
        }
      }

      const message = finalError instanceof Error ? finalError.message : 'Unable to load categories.'
      setCategories((prev) => (lastGoodCategoriesRef.current.length ? lastGoodCategoriesRef.current : prev))
      setError(lastGoodCategoriesRef.current.length ? null : message)
      setLoading(false)
      if (!lastGoodCategoriesRef.current.length && typeof window !== 'undefined') {
        retryTimerRef.current = window.setTimeout(() => setRetryReady(true), 20000)
      }
    }

    void load()

    return () => {
      cancelled = true
      controller.abort()
      if (retryTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(retryTimerRef.current)
      }
    }
  }, [open, retryTick])

  React.useEffect(() => {
    if (!open) {
      setSelectedCategoryIds([])
      setSearch('')
      setItemsPerPage(5)
      setStartCollapsed(false)
      setSaving(false)
      setSnackbar(null)
      return
    }

    setSelectedCategoryIds((current) => current.filter((id) => !existingCategoryIds.has(id)))
  }, [existingCategoryIds, open])

  const filteredCategories = React.useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return categories
    }
    return categories.filter((category) => {
      const name = category.name?.toLowerCase() ?? ''
      const slug = category.slug?.toLowerCase() ?? ''
      return name.includes(query) || slug.includes(query)
    })
  }, [categories, search])

  const handleRetryCategories = React.useCallback(() => {
    setRetryReady(false)
    setRetryTick((value) => value + 1)
  }, [])

  const handleToggleCategory = (categoryId: number) => {
    if (saving) {
      return
    }

    setSelectedCategoryIds((current) => {
      if (current.includes(categoryId)) {
        return current.filter((id) => id !== categoryId)
      }
      return [...current, categoryId]
    })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const unique = Array.from(new Set(selectedCategoryIds))
    if (unique.length === 0) {
      return
    }

    const duplicates: string[] = []
    const toAdd: Array<{
      categoryId: number
      itemsPerPage: number
      collapsed?: boolean
      categoryName?: string
      categorySlug?: string
    }> = []

    unique.forEach((categoryId) => {
      if (existingCategoryIds.has(categoryId)) {
        const name = categoriesById.get(categoryId)?.name ?? 'This category'
        duplicates.push(name)
        return
      }
      const category = categoriesById.get(categoryId)
      const name = category?.name?.trim()
      const slug = category?.slug?.trim()
      toAdd.push({
        categoryId,
        itemsPerPage,
        collapsed: startCollapsed,
        ...(name ? { categoryName: name } : {}),
        ...(slug ? { categorySlug: slug } : {}),
      })
    })

    if (duplicates.length > 0) {
      const message =
        duplicates.length === 1
          ? `${duplicates[0]} is already on your dashboard.`
          : `${duplicates.join(', ')} are already on your dashboard.`
      setSnackbar({ message, severity: 'info' })
    }

    if (toAdd.length === 0) {
      return
    }

    setSaving(true)
    try {
      await onAddCategoryCards(toAdd)
      setSelectedCategoryIds([])
      onClose()
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Could not save dashboard changes. Please try again.'
      setSnackbar({ message, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (saving) {
      return
    }
    onClose()
  }

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const next = Number(event.target.value)
    setItemsPerPage(clampItemsPerPage(next))
  }

  const handleSnackbarClose = () => {
    setSnackbar(null)
  }

  const disableAdd = selectedCategoryIds.length === 0 || saving

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle id={titleId}>Customize Dashboard</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} id={descriptionId}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Add category cards
              </Typography>
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search categories"
                size="small"
                fullWidth
                disabled={loading || saving}
                InputLabelProps={{ shrink: false }}
              />
              <Box
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  mt: 1,
                  p: 0.5,
                  minHeight: 160,
                  maxHeight: 320,
                  overflowY: 'auto',
                }}
              >
                {loading ? (
                  <Stack spacing={1} sx={{ p: 1 }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton key={index} variant="rounded" height={44} />
                    ))}
                  </Stack>
                ) : error ? (
                  <Stack spacing={1} sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {error}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={handleRetryCategories}>
                        Retry
                      </Button>
                    </Box>
                  </Stack>
                ) : retryReady ? (
                  <Stack spacing={1} sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Categories did not load. Retry?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={handleRetryCategories}>
                        Retry
                      </Button>
                    </Box>
                  </Stack>
                ) : filteredCategories.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No categories found.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {filteredCategories.map((category) => {
                      const disabled = existingCategoryIds.has(category.id)
                      const selected = selectedCategoryIds.includes(category.id)
                      const count = typeof category.count === 'number' ? category.count : null
                      return (
                        <ListItemButton
                          key={category.id}
                          onClick={() => handleToggleCategory(category.id)}
                          disabled={disabled || saving}
                          selected={selected}
                          sx={{
                            borderRadius: 2,
                            my: 0.25,
                            px: 1,
                            border: (theme) =>
                              `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                            <Checkbox
                              edge="start"
                              checked={selected}
                              disabled={disabled || saving}
                              tabIndex={-1}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => handleToggleCategory(category.id)}
                              inputProps={{ 'aria-label': category.name }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={category.name}
                            secondary={category.slug}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          {count !== null ? <Chip label={count} size="small" color="default" /> : null}
                        </ListItemButton>
                      )
                    })}
                  </List>
                )}
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 160 }} disabled={saving}>
                <InputLabel id="prep-dashboard-items-per-page-label">Items per card</InputLabel>
                <Select
                  labelId="prep-dashboard-items-per-page-label"
                  label="Items per card"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  renderValue={(value) => String(value)}
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={startCollapsed}
                    onChange={(event) => setStartCollapsed(event.target.checked)}
                    disabled={saving}
                  />
                }
                label="Start collapsed"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={disableAdd}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Add cards
          </Button>
        </DialogActions>
      </Box>
      <Snackbar open={Boolean(snackbar)} autoHideDuration={4000} onClose={handleSnackbarClose}>
        {snackbar ? (
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Dialog>
  )
}

export default PrepDashboardSettingsModal
